import { type FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type UseFormSetValue } from 'react-hook-form';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

// MUI
import InputAdornment from '@mui/material/InputAdornment';
import SaveRounded from '@mui/icons-material/SaveRounded';
import IconButton from '@mui/material/IconButton';
import ToggleOff from '@mui/icons-material/VisibilityOffRounded';
import ToggleOn from '@mui/icons-material/VisibilityRounded';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import UndoRounded from '@mui/icons-material/UndoRounded';

// common
import SchemaForm from 'common/SchemaForm';
import { EMAIL_REGEX, PASSWORD, USERNAME } from 'config/regex';
import type { ElementFormProps, IRawElement } from 'common/SchemaForm/utils';
import { ROUTES_PATH } from 'layout/constants';
import { datePickerCustomStyle } from 'common/SchemaForm/element/datepicker-element/utils';

// screen
import type { AccountInfo, PasswordField } from 'modules/account/types';
import { useAccountCreateContext } from 'modules/account/create/AccountCreateContext';

type ExpirationPeriod = 'oneWeek' | 'oneMonth' | 'oneYear' | '';

interface ExpirationOption {
  value: ExpirationPeriod;
  label: string;
}

const expirationOptions: ExpirationOption[] = [
  { value: 'oneWeek', label: 'account.expiration.onWeek' },
  { value: 'oneMonth', label: 'account.expiration.oneMonth' },
  { value: 'oneYear', label: 'account.expiration.oneYear' },
];

type Unit = 'd' | 'y';
type ExpirationConfig = { value: number; unit: Unit };
const EXPIRATION_PERIODS: Partial<Record<ExpirationPeriod, ExpirationConfig>> = {
  oneWeek: { value: 7, unit: 'd' },
  oneMonth: { value: 30, unit: 'd' },
  oneYear: { value: 1, unit: 'y' },
};

const StepOne: FC = () => {
  const { state, handleSetAccountInfo } = useAccountCreateContext();
  const { accountInfo } = state;
  const [toggle, setToggle] = useState({ password: false, passwordRetype: false });
  const [expiration, setExpiration] = useState<ExpirationPeriod>('');
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const toggleFields = (key: PasswordField) => setToggle((old) => ({ ...old, [key]: !old[key] }));

  const handleSelectExpiration = (
    exp: ExpirationPeriod,
    setField: UseFormSetValue<AccountInfo>
  ) => {
    const { value, unit } = EXPIRATION_PERIODS[exp] || {};
    if (value) setField('expiredAt', dayjs(new Date()).add(value, unit).toISOString());
    setExpiration(exp);
  };

  const backToAccountsList = () => {
    navigate(['', ROUTES_PATH.ad.index, ROUTES_PATH.account.index].join('/'));
  };

  const renderField = (
    name: PasswordField
  ): ElementFormProps<AccountInfo, typeof name> | IRawElement<AccountInfo, typeof name> => ({
    mode: 'text-field',
    type: toggle[name] ? 'text' : 'password',
    label: formatMessage({ id: `account.${name}` }),
    placeholder: formatMessage({ id: `account.${name}` }),
    propsWrapper: { tablet: 6 },
    rules: {
      required: true,
      minLength: { value: 8, message: formatMessage({ id: 'account.error.minLength' }) },
      pattern: { value: PASSWORD, message: formatMessage({ id: 'account.error.invalidPassword' }) },
      validate: (_fieldValue, { password, passwordRetype }) =>
        password !== passwordRetype
          ? formatMessage({ id: 'account.error.notSamePassword' })
          : undefined,
    },
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={() => toggleFields(name)} color="primary">
            {toggle[name] ? <ToggleOff /> : <ToggleOn />}
          </IconButton>
        </InputAdornment>
      ),
    },
  });

  return (
    <SchemaForm<AccountInfo>
      formHookProps={{ values: accountInfo }}
      onSubmit={handleSetAccountInfo}
      formProps={{
        sx: {
          '& .MuiGrid-root.MuiGrid-container': {
            '& .MuiGrid-root.MuiGrid-item': {
              '& .MuiGrid-root.MuiGrid-container': { display: 'flex', justifyContent: 'center' },
            },
          },
        },
      }}
      schema={{
        fields: ({ methods: { setValue }, valuesField: { status } }) => ({
          username: {
            mode: 'text-field',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.accountName' }),
            placeholder: formatMessage({ id: 'account.accountName' }),
            rules: {
              required: true,
              pattern: {
                value: USERNAME,
                message: formatMessage({ id: 'account.error.invalidUserName' }),
              },
            },
          },
          blank1: { mode: 'node' },
          email: {
            mode: 'text-field',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'email' }),
            placeholder: formatMessage({ id: 'email' }),
            rules: {
              required: true,
              pattern: { value: EMAIL_REGEX, message: formatMessage({ id: 'invalid_email' }) },
            },
          },
          blank2: { mode: 'node' },
          password: renderField('password'),
          blank3: { mode: 'node' },
          passwordRetype: renderField('passwordRetype'),
          blank6: { mode: 'node' },
          expiredAt: {
            mode: 'datePicker',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.expiredAt' }),
            slotProps: {
              textField: {
                placeholder: status
                  ? formatMessage({ id: 'account.expiration.noExpiration' })
                  : 'DD/MM/YYYY',
                ...datePickerCustomStyle.slotProps.textField,
              },
            },
            rules: { required: !status },
            disablePast: true,
            disabled: status,
          },
          blank7: { mode: 'node' },
          customFields: {
            mode: 'raw',
            propsWrapper: { tablet: 6 },
            render: () => (
              <Box sx={{ display: 'flex' }}>
                {expirationOptions.map(({ value, label }) => (
                  <Chip
                    key={value}
                    label={<FormattedMessage id={label} />}
                    sx={{
                      marginX: '4px',
                      color: expiration === value ? '#fff' : 'primary.main',
                      backgroundColor: expiration === value ? 'primary.main' : 'none',
                      borderColor: 'primary.main',
                    }}
                    variant="outlined"
                    disabled={status}
                    onClick={() => handleSelectExpiration(value, setValue)}
                  />
                ))}
              </Box>
            ),
          },
          blank8: { mode: 'node' },
          status: {
            mode: 'switch',
            propsWrapper: { tablet: 6 },
            label: formatMessage({ id: 'account.expiration.noExpiration' }),
            labelPlacement: 'top',
            onChange: () => {
              setExpiration('');
              setValue('expiredAt', '');
            },
          },
          blank9: { mode: 'node' },
          back: {
            mode: 'button',
            variant: 'outlined',
            startIcon: <UndoRounded />,
            children: formatMessage({ id: 'back' }),
            onClick: backToAccountsList,
            propsWrapper: { mobile: true, display: 'flex', justifyContent: 'end' },
          },
          submit: {
            mode: 'button',
            type: 'submit',
            variant: 'contained',
            startIcon: <SaveRounded />,
            children: formatMessage({ id: 'continue' }),
            propsWrapper: { mobile: undefined },
          },
        }),
      }}
    />
  );
};

export default StepOne;
